import { computed, ref } from 'vue'

import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type { MultiselectSearchOption } from '@shared/ui/fields'
import type { DisciplineTaskCommentsFilterParams } from './filter-params'

import { useFilterDateRange } from '@shared/lib/filters'

import { useFilterDiscipline } from './filter-discipline.hook'
import { buildQueryObject, parseFilterParams } from './filter-params'

interface DisciplineTaskCommentsFiltersReturn {
  filterResetKey: Ref<number>
  createdRange: WritableComputedRef<Date[] | null>
  disciplineOptions: ComputedRef<MultiselectSearchOption[]>
  disciplineInitialValue: ComputedRef<string[]>
  handleDisciplineSelected: (selected: MultiselectSearchOption[]) => void
  handleSearch: () => void
  handleClear: () => void
}

export const useDisciplineTaskCommentsFilters = (): DisciplineTaskCommentsFiltersReturn => {
  const route = useRoute()
  const router = useRouter()

  const filterResetKey = ref(0)

  const initialParams = parseFilterParams(route.query)

  const {
    range: createdRange,
    from: createdFrom,
    to: createdTo,
  } = useFilterDateRange({
    initialFrom: initialParams.created_from,
    initialTo: initialParams.created_to,
  })

  const {
    options: disciplineOptions,
    initialValue: disciplineInitialValue,
    handleSelected: handleDisciplineSelected,
    query: disciplineQuery,
  } = useFilterDiscipline({
    initial: initialParams.discipline_id,
    defaultIds: [],
  })

  const queryParams = computed<DisciplineTaskCommentsFilterParams>(() => ({
    discipline_id: disciplineQuery.value,
    created_from: createdFrom.value,
    created_to: createdTo.value,
  }))

  const handleSearch = (): void => {
    const query = buildQueryObject(queryParams.value)
    void router.push({ query })
  }

  const handleClear = (): void => {
    createdRange.value = null
    disciplineQuery.value = []
    filterResetKey.value++

    void router.push({ query: {} })
  }

  return {
    filterResetKey,
    createdRange,
    disciplineOptions,
    disciplineInitialValue,
    handleDisciplineSelected,
    handleSearch,
    handleClear,
  }
}
